import React, { useCallback, useEffect, useState } from 'react';
import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CAlert,
  CSpinner,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CButton,
  CFormInput,
} from '@coreui/react';
import { getPaidUninsured, getPaidUninsuredSummary } from '../../../utils/backend';

/**
 * PAID BUT UNINSURED — Ansa-Support#49.
 *
 * Orders where the customer's money was taken and no cover note exists. The
 * partner asked for this specifically:
 *
 *   "available to all ANSA staff through the admin system so that the relevant
 *    team can see and act on any case where a customer has paid but no policy
 *    has been issued."
 *
 * We have detected these correctly every night since 4 August; the report went
 * to a log file on the production server that nobody reads. This screen is the
 * delivery that was missing, which is why the design below is preoccupied with
 * one thing: never letting a failure look like an all-clear.
 *
 * A monitor for this condition once reported "0 stuck" every twenty minutes for
 * nine days while a customer sat paid and uninsured. So:
 *   - a load failure shows an explicit error, never an empty table;
 *   - "0 stuck" is stated as a positive, dated all-clear rather than a blank;
 *   - the count of the invisible failure shape is shown separately, because that
 *     is the one two of our automated sweeps cannot see at all.
 */

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const NO_POLICY_ROW = 'No policy record';

// Anything past a day is beyond "it will settle by itself".
const AGE_ALARM_HOURS = 24;

const ageLabel = (hours) => {
  const h = Number(hours) || 0;

  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);

  return `${days}d ${h % 24}h`;
};

/**
 * THE DEADLINE — why this column outranks the age column.
 *
 * Tune refuses to issue once the cover start date has passed. So that date is the moment a
 * case stops being fixable and becomes a refund conversation. On 27 Aug 2026 two orders that
 * had been stuck for a similar length of time had deadlines seven weeks apart: one had until
 * 17 October, the other until that midnight. Sorted by age they sat side by side, looking
 * equally urgent, and the wrong one got worked first.
 *
 * The server sorts by this now — savable soonest-first, then cases it cannot date, then the
 * expired. The screen colours them to match and never reorders the list itself: two different
 * sort orders for the same data is how an operator loses trust in both.
 */
const DEADLINE_COLOURS = {
  expired: '#6B7280',
  critical: '#DC2626',
  later: '#111827',
  unknown: '#B45309',
};

/**
 * True once the API is the build that carries the deadline fields.
 *
 * The screen can be deployed before or after the backend that feeds it, and a half-deployed
 * pair must degrade to the previous screen rather than render "0 unfixable" or a column of
 * dashes — a confident zero is exactly the failure this whole page exists to prevent.
 */
const hasDeadlineData = (s) => Boolean(s) && typeof s.critical_count === 'number';

const PaidUninsured = () => {
  const [summary, setSummary] = useState({ loading: true, error: null, data: null });
  const [list, setList] = useState({ loading: true, error: null, data: null });
  const [search, setSearch] = useState('');
  const [checkedAt, setCheckedAt] = useState(null);

  const load = useCallback(async () => {
    setSummary({ loading: true, error: null, data: null });
    setList({ loading: true, error: null, data: null });

    try {
      const response = await getPaidUninsuredSummary({});

      setSummary({ loading: false, error: null, data: (response && response.data) ?? response });
    } catch (err) {
      setSummary({ loading: false, error: err.message || 'An error occurred', data: null });
    }

    try {
      const response = await getPaidUninsured({});

      setList({ loading: false, error: null, data: (response && response.data) ?? response });
    } catch (err) {
      setList({ loading: false, error: err.message || 'An error occurred', data: null });
    }

    setCheckedAt(new Date());
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = list.data || [];
  const needle = String(search).trim().toUpperCase();
  const filtered = needle
    ? rows.filter((r) =>
        [r.order_number, r.insurer_name, r.policy_state].some((v) =>
          String(v || '')
            .toUpperCase()
            .includes(needle),
        ),
      )
    : rows;

  const s = summary.data;

  // An all-clear requires BOTH requests to have succeeded. If the summary says
  // zero but the list failed, we have half an answer, and half an answer must not
  // be shown in green next to a red error — the reader would have to decide which
  // to believe. Withholding the all-clear is the safe direction.
  const isClear = !summary.loading && !summary.error && !list.loading && !list.error && s && s.total_count === 0;

  return (
    <div>
      <CRow className="mb-3 align-items-center">
        <CCol>
          <h5 className="mb-0">Paid but Uninsured</h5>
          <div style={{ fontSize: '12px', color: '#8A93A2' }}>
            Orders where payment completed and no cover note was issued. Read-only — nothing here retries issuance or touches a payment.
          </div>
        </CCol>
        <CCol xs="auto">
          <CButton size="sm" color="secondary" variant="outline" onClick={load} disabled={summary.loading || list.loading}>
            Refresh
          </CButton>
        </CCol>
      </CRow>

      {/* A failure must never read as an all-clear. */}
      {summary.error && (
        <CAlert color="danger" style={{ fontSize: '13px' }}>
          <strong>Could not check for paid-but-uninsured orders.</strong>
          <div>{summary.error}</div>
          <div className="mt-1" style={{ fontSize: '12px' }}>
            This is <strong>not</strong> an all-clear. The number of affected customers is unknown — please retry, and escalate if it keeps
            failing.
          </div>
        </CAlert>
      )}

      {summary.loading && (
        <CCard className="border-light">
          <CCardBody className="text-center py-5">
            <CSpinner size="sm" /> <span style={{ fontSize: '13px', color: '#555E67' }}>Checking…</span>
          </CCardBody>
        </CCard>
      )}

      {isClear && (
        <CAlert color="success" style={{ fontSize: '13px' }}>
          <strong>No paid-but-uninsured orders found.</strong>
          <div style={{ fontSize: '12px' }}>
            Checked {checkedAt ? checkedAt.toLocaleString() : 'just now'} across all insurers, with no time limit — this covers both the
            case where a policy record is stuck and the case where none was created at all.
          </div>
        </CAlert>
      )}

      {!summary.loading && !summary.error && s && s.total_count > 0 && (
        <CRow className="g-3">
          <CCol xs={12} sm={6} lg={3}>
            <CCard className="border-light h-100">
              <CCardBody>
                <div style={{ fontSize: '13px', color: '#555E67' }}>Affected Orders</div>
                <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#DC2626' }}>{s.total_count.toLocaleString()}</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol xs={12} sm={6} lg={3}>
            <CCard className="border-light h-100">
              <CCardBody>
                <div style={{ fontSize: '13px', color: '#555E67' }}>Value at Risk</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold' }}>RM {money(s.total_value)}</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol xs={12} sm={6} lg={3}>
            <CCard className="border-light h-100">
              <CCardBody>
                {hasDeadlineData(s) ? (
                  <>
                    {/* The figure the partner asked for: not "10 affected" but "2 of them
                        become unfixable within 48 hours". */}
                    <div style={{ fontSize: '13px', color: '#555E67' }}>Becoming Unfixable</div>
                    <div style={{ fontSize: '26px', fontWeight: 'bold', color: s.critical_count > 0 ? '#DC2626' : '#111827' }}>
                      {s.critical_count.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '11px', color: '#8A93A2' }}>
                      within {s.critical_window_hours || 48}h
                      {typeof s.soonest_deadline_hours === 'number' ? ` — soonest ${Math.floor(s.soonest_deadline_hours)}h left` : ''}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '13px', color: '#555E67' }}>Oldest Case</div>
                    <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{ageLabel(s.oldest_age_hours)}</div>
                    <div style={{ fontSize: '11px', color: '#8A93A2' }}>since the customer paid</div>
                  </>
                )}
              </CCardBody>
            </CCard>
          </CCol>
          <CCol xs={12} sm={6} lg={3}>
            <CCard className="border-light h-100">
              <CCardBody>
                <div style={{ fontSize: '13px', color: '#555E67' }}>No Policy Record At All</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{s.missing_policy_row_count.toLocaleString()}</div>
                <div style={{ fontSize: '11px', color: '#8A93A2' }}>
                  the shape our automated sweeps cannot see — these need manual attention
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {/* Expired cases cannot be rescued by working faster, so they are stated apart from the
          countdown rather than mixed into it. They stay on the list below: an order whose cover
          start has passed still has a customer owed either cover or their money back. */}
      {!summary.loading && !summary.error && hasDeadlineData(s) && s.expired_count > 0 && (
        <CAlert color="warning" className="mt-3" style={{ fontSize: '13px' }}>
          <strong>
            {s.expired_count.toLocaleString()} {s.expired_count === 1 ? 'order has' : 'orders have'} passed the cover start date.
          </strong>
          <div style={{ fontSize: '12px' }}>
            These can no longer be issued — the insurer refuses to back-date — so each needs a refund decision rather than another retry.
            They are at the bottom of the list, marked EXPIRED.
          </div>
        </CAlert>
      )}

      {!summary.loading && !summary.error && hasDeadlineData(s) && s.unknown_deadline_count > 0 && (
        <div className="mt-3" style={{ fontSize: '12px', color: '#B45309' }}>
          {s.unknown_deadline_count.toLocaleString()} {s.unknown_deadline_count === 1 ? 'order has' : 'orders have'} no cover start date we
          can read. That is not the same as having time left — it means we cannot tell, so they are listed above the expired ones for
          someone to check by hand.
        </div>
      )}

      {!summary.loading && !summary.error && s && s.total_count > 0 && s.by_insurer && (
        <div className="mt-3" style={{ fontSize: '13px' }}>
          {hasDeadlineData(s) && (
            <span style={{ color: '#8A93A2' }}>
              Oldest case {ageLabel(s.oldest_age_hours)} since payment ·{' '}
            </span>
          )}
          <strong>By insurer:</strong>{' '}
          {Object.entries(s.by_insurer)
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => `${name} (${count})`)
            .join(' · ')}
        </div>
      )}

      <CCard className="border-light mt-3">
        <CCardBody style={{ overflowX: 'auto' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <span>
              Affected Orders
              {hasDeadlineData(s) && (
                <span style={{ fontSize: '12px', color: '#8A93A2', fontWeight: 'normal' }}>
                  {' '}
                  — ordered by how long is left to fix each one, soonest first
                </span>
              )}
            </span>
            <CFormInput
              size="sm"
              style={{ width: '220px' }}
              placeholder="Search order or insurer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search affected orders"
            />
          </div>

          {list.loading && (
            <div className="text-center py-5">
              <CSpinner size="sm" /> <span style={{ fontSize: '13px', color: '#555E67' }}>Loading…</span>
            </div>
          )}

          {!list.loading && list.error && (
            <CAlert color="danger" className="mt-3 mb-0" style={{ fontSize: '13px' }}>
              <strong>Could not load the list.</strong>
              <div>{list.error}</div>
              <div className="mt-1" style={{ fontSize: '12px' }}>
                An empty list is not being shown, because we do not know that it is empty.
              </div>
            </CAlert>
          )}

          {!list.loading && !list.error && rows.length === 0 && (
            <div className="text-center py-5" style={{ fontSize: '13px', color: '#16A34A' }}>
              Nothing stuck. Every completed payment has a cover note.
            </div>
          )}

          {!list.loading && !list.error && rows.length > 0 && (
            <CTable small hover responsive className="mt-3" style={{ fontSize: '13px' }}>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Order</CTableHeaderCell>
                  <CTableHeaderCell>Insurer</CTableHeaderCell>
                  <CTableHeaderCell>Policy State</CTableHeaderCell>
                  <CTableHeaderCell>Cover Starts</CTableHeaderCell>
                  <CTableHeaderCell>Time Left To Fix</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Value (RM)</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Exposed For</CTableHeaderCell>
                  <CTableHeaderCell>Paid At</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {filtered.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={8} className="text-center py-4" style={{ color: '#8A93A2' }}>
                      No affected order matches “{search}”. {rows.length.toLocaleString()} are still affected — clear the search to see
                      them.
                    </CTableDataCell>
                  </CTableRow>
                )}
                {filtered.map((row) => (
                  <CTableRow key={row.order_number}>
                    <CTableDataCell>{row.order_number}</CTableDataCell>
                    <CTableDataCell>{row.insurer_name || '—'}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color={row.policy_state === NO_POLICY_ROW ? 'danger' : 'warning'}>{row.policy_state}</CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      {row.cover_start_display || '—'}
                      {/* Where the date came from is worth a word: with no policy row the only
                          date we hold is the customer's quote, which is the cover they intended
                          to buy rather than a date any insurer has acknowledged. */}
                      {row.cover_start_source === 'quote' && (
                        <div style={{ fontSize: '11px', color: '#8A93A2' }}>from the quote</div>
                      )}
                    </CTableDataCell>
                    <CTableDataCell
                      style={{
                        color: DEADLINE_COLOURS[row.deadline_state] || undefined,
                        fontWeight: row.deadline_state === 'critical' ? 'bold' : undefined,
                      }}
                    >
                      {row.deadline_label || '—'}
                    </CTableDataCell>
                    <CTableDataCell className="text-end">{money(row.order_value)}</CTableDataCell>
                    <CTableDataCell
                      className="text-end"
                      style={{ color: row.age_hours >= AGE_ALARM_HOURS ? '#DC2626' : undefined, fontWeight: 'bold' }}
                    >
                      {ageLabel(row.age_hours)}
                    </CTableDataCell>
                    <CTableDataCell>{row.paid_at ? new Date(row.paid_at).toLocaleString() : '—'}</CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}

          <div className="mt-3" style={{ fontSize: '12px', color: '#8A93A2' }}>
            Customer names and contact details are deliberately not shown here. Open the order to reach them, so personal data stays on the
            screens already controlled for it.
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default PaidUninsured;
